<div class="space-y-6">
  <app-page-header
    [backTo]="['/permissions']"
    backLabel="Retour aux permissions"
    title="Nouvelle permission"
    subtitle="Lie une ou plusieurs scopes à des policies. La création peut échouer si une policy nommée n'existe pas — il faut alors d'abord créer le rôle correspondant."
  />

  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-6">
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Définition</h2>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label for="p-name" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Nom <span class="text-error-500">*</span></label>
          <input id="p-name" type="text" formControlName="name" placeholder="ex. user_manage" class="h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 font-mono text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            [class.border-error-500]="showError('name')"
            [class.border-gray-300]="!showError('name')"
            [class.dark:border-gray-700]="!showError('name')" />
          @if (showError('name')) { <p class="mt-1.5 text-xs text-error-500">Champ obligatoire.</p> }
        </div>
        <div>
          <label for="p-display" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Libellé</label>
          <input id="p-display" type="text" formControlName="displayName" placeholder="Libellé affiché" class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800" />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Type</label>
          <app-pretty-select formControlName="type" [options]="typeOptions" />
        </div>
        <div>
          <label for="p-resource-type" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Resource type (optionnel)</label>
          <input id="p-resource-type" type="text" formControlName="resourceType" placeholder="urn:nawam:resources:user" class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 font-mono text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800" />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Stratégie de décision</label>
          <app-pretty-select formControlName="decisionStrategy" [options]="decisionStrategyOptions" />
        </div>
        <div class="flex items-center">
          <label class="inline-flex cursor-pointer items-center gap-3">
            <input type="checkbox" formControlName="ownerManagedAccess" class="peer sr-only" />
            <span class="relative h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-brand-500 dark:bg-gray-700">
              <span class="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-theme-xs transition peer-checked:translate-x-5"></span>
            </span>
            <span class="text-sm text-gray-700 dark:text-gray-300">Owner-managed access (UMA)</span>
          </label>
        </div>
      </div>
    </section>

    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">Scopes ciblés</h2>
      <p class="mb-4 text-xs text-gray-500 dark:text-gray-400">Actions accordées par cette permission.</p>
      @if (loadingScopes()) {
        <p class="text-xs text-gray-500 dark:text-gray-400">Chargement…</p>
      } @else if (availableScopes().length === 0) {
        <p class="text-xs text-gray-500 dark:text-gray-400">Aucun scope. Crée-en d'abord depuis l'onglet Scopes.</p>
      } @else {
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          @for (s of availableScopes(); track s.id || s.name) {
            <label class="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 transition hover:border-brand-300 hover:bg-brand-50 dark:border-gray-700 dark:hover:border-brand-700 dark:hover:bg-brand-500/10"
              [class.border-brand-500]="isScopeSelected(s.name)"
              [class.bg-brand-50]="isScopeSelected(s.name)"
              [class.dark:border-brand-700]="isScopeSelected(s.name)"
              [class.dark:bg-brand-500/10]="isScopeSelected(s.name)">
              <input type="checkbox" [checked]="isScopeSelected(s.name)" (change)="toggleScope(s.name)" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 dark:border-gray-600" />
              <span class="font-mono text-xs text-gray-700 dark:text-gray-300">{{ s.name }}</span>
            </label>
          }
        </div>
      }
    </section>

    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">Policies à attacher</h2>
      <p class="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Noms de policies (un par ligne ou séparés par virgule). Pour une policy de rôle, le nom est typiquement <code class="font-mono">nom-du-role-policy</code> ou identique au rôle. Tu peux aussi laisser vide et attacher les policies plus tard depuis la fiche.
      </p>
      <textarea formControlName="policiesRaw" rows="3" placeholder="ROLE_ADMIN-policy, ROLE_OPERATOR-policy" class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 font-mono text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"></textarea>
    </section>

    @if (serverError(); as msg) {
      <div class="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
    }

    <div class="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
      <button type="button" (click)="onCancel()" [disabled]="submitting()" class="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="submitting() || form.invalid" class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
        @if (submitting()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          Création…
        } @else { Créer la permission }
      </button>
    </div>
  </form>
</div>
